import { useEffect, useState } from 'react';
import { subscribeCollection, subscribeCollections } from '@/data/collectionsRepo';
import type { CollectionType } from '@/types/types_collection';

/**
 * Subscribes to all collections in real time via a Firestore snapshot listener,
 * so the UI reflects data changes without a manual reload.
 */
export function useCollections() {
  const [collections, setCollections] = useState<CollectionType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeCollections((list) => {
      setCollections(list);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  return { collections, loading };
}

/** Subscribes to a single collection by id in real time. */
export function useCollection(id: string | null | undefined) {
  const [collection, setCollection] = useState<CollectionType>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      // Defer to a microtask so we don't setState synchronously in the effect.
      void Promise.resolve().then(() => {
        if (cancelled) return;
        setCollection(undefined);
        setLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }
    const unsubscribe = subscribeCollection(id, (c) => {
      setCollection(c);
      setLoading(false);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [id]);

  return { collection, loading };
}
